import { useEffect, useRef } from 'react'

import { useQuery } from '@apollo/client'
import { useReactiveVar } from '@apollo/client'
import { useParams } from 'react-router-dom'

import {
  ExternalPaymentRecordsDocument,
  ManualTransactionsDocument,
  ManualTransactionsQuery,
  OrderPaymentRecordsDocument,
  anonymousOrderQueryDocument,
} from '__generated__/atlas-types'

import { fromGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'
import { useIsPageVisible } from 'hooks/useIsPageVisible'
import {
  getPaymentInfo,
  transformPaymentRecords,
} from 'payments/payments.transformations'

import { Payment, TransformedPaymentRecords } from '../types'
import { refetchRemainingBalance } from './components/helpers'
import {
  BANK_TRANSFER_METHOD_NAME,
  bankTransferSessionAwaitingInstructions,
  iframePaymentSession,
  temporaryPendingPayments,
} from './store'
import { mergePaymentsWithPendingPayments, transformOrder } from './utils'
import { getExternalPaymentData } from 'features/Orders/utils/helpers'

const ANONYMOUS_ORDER_QUERY_REDIRECT = atlas`#graphql
  query anonymousOrderQueryRedirect($uuid: String!) {
    public {
      getOrderByInvoice(invoiceId: $uuid) {
        id
        brand{
          id
        }
        retailer {
          id
        }
      }
    }
  }
`

export const useRedirectData = ({
  accountId,
  uuid,
}: {
  accountId: number
  uuid: string
}) => {
  const { data, loading } = useQuery(ANONYMOUS_ORDER_QUERY_REDIRECT, {
    client: atlasClient,
    variables: { uuid },
    skip: !accountId || !uuid,
  })

  const redirectData = {
    orderId: fromGlobalId(data?.public?.getOrderByInvoice?.id).id,
    brandId: fromGlobalId(data?.public?.getOrderByInvoice?.brand?.id).id,
    retailerId: fromGlobalId(data?.public?.getOrderByInvoice?.retailer?.id).id,
  }

  return {
    redirectData,
    loading,
  }
}

atlas`#graphql
  query anonymousOrderQuery($uuid: String!) {
    public {
      getOrderByInvoice(invoiceId: $uuid) {
        id
        deliveryStart
        deliveryComplete
        status
        referenceNumber
        shippingEmail
        taxAmount
        discountAmount
        shippingPrice
        salesRepresentative {
          name
          email
        }
        lineItems {
          discountedPrice
          totalUnits
          totalPrice
          totalRetailPrice
        }
        shippingPhone
        shippingCompany
        shippingFirstAddress
        shippingSecondAddress
        shippingState
        shippingZip
        shippingCity
        shippingState
        shippingCountry
        priceType {
          currency {
            decimals
            code
            symbol
          }
        }
        brand {
          id
          displayName
        }
        retailer {
          displayName
          id
        }
        webOrderLookupId
        seasonYear {
          year
          season {
            name
          }
        }
        collections {
          edges {
            node {
              name
            }
          }
        }
        taxIdNumber
        billingCountry
        billingCompany
        billingFirstAddress
        billingSecondAddress
        billingState
        billingZip
        billingCity
        billingPhone
        modified
        lastModifier {
          email
        }
        door {
          id
          name
        }
        warehouse {
          id
          name
        }
        paymentMethod {
          id
          paymentName
        }
        financials {
          ...OrderFinancials
        }
      }
    }
  }
`

export const useAnonymousOrder = () => {
  const { uuid } = useParams<{ uuid: string }>()
  const { data, loading } = useQuery(anonymousOrderQueryDocument, {
    client: atlasClient,
    variables: { uuid: uuid ?? '' },
    skip: !uuid,
  })

  const order = transformOrder(data?.public?.getOrderByInvoice)

  return {
    orderQueryLoading: loading,
    order,
    billingAddressDictionary: order?.billingAddressDictionary,
  }
}

atlas`#graphql
  query OrderPaymentRecords($orderIds: [ID!]!, $accountId: ID!) {
    paymentRecords(orderIds: $orderIds, accountId: $accountId) {
      id
      orderId
      amount
      currency
      status
      created
      localAmount
      localCurrency
      externalIdentifier
      errorMessage
      paymentMethod {
        type
        ... on BankAccount {
          bankName
        }
        ... on CreditCard {
          brand
        }
      }
    }
  }
`

const POLL_INTERVAL = 1000 * 5

const MANUAL_TRANSACTION_POLL_INTERVAL = 1000 * 30

export type UseGetPaymentsHistory = {
  data: {
    payments: Payment[]
    total: number
    localTotal: number
  }
  loading: boolean
  error?: Error
}

type UseGetPaymentsHistoryInput = {
  accountId?: string
  orderId?: string
  externalPaymentId?: string
}

const getRemainingPendingPayments = ({
  queryPaymentRecords,
  pendingPaymentRecords,
}: {
  queryPaymentRecords: TransformedPaymentRecords[]
  pendingPaymentRecords: TransformedPaymentRecords[]
}) => {
  // We need to filter all the payments that we have already fetched
  // and are present in the query payments records
  return pendingPaymentRecords.filter(
    (curr: TransformedPaymentRecords) =>
      !queryPaymentRecords.find(
        (rec: TransformedPaymentRecords) =>
          rec.externalIdentifier === curr.externalIdentifier,
      ),
    [],
  )
}

const updateTemporaryPayments = ({
  orderPaymentData,
  paymentsInWaiting,
  uuid,
  externalPaymentUUID,
}: {
  orderPaymentData: TransformedPaymentRecords[]
  paymentsInWaiting: TransformedPaymentRecords[]
  uuid?: string
  externalPaymentUUID?: string
}) => {
  const remainingPendingPayments = getRemainingPendingPayments({
    queryPaymentRecords: orderPaymentData,
    pendingPaymentRecords: paymentsInWaiting,
  })

  // If we have a different quantity of pending payments, that means that we have fetched
  // correctly the real temporary payment and we need to update the Reactive variables we
  // are using to store those payments, we need to update this because the remaining balance
  // is already update with the transaction amount, we no longer need this fake payment
  // Also we need to only update this in private payments, because in the public invoice
  // We are still calculating the total amount and this update is not necessary.
  if (
    remainingPendingPayments.length !== paymentsInWaiting.length &&
    uuid === undefined &&
    externalPaymentUUID === undefined
  ) {
    temporaryPendingPayments(remainingPendingPayments)
  }
}

export const useGetPaymentsHistory = ({
  orderId = '',
  externalPaymentId = '',
  accountId = '',
}: UseGetPaymentsHistoryInput): UseGetPaymentsHistory => {
  const { externalPaymentUUID, uuid } = useParams<{
    externalPaymentUUID?: string
    uuid?: string
  }>()
  const isVisible = useIsPageVisible()
  const paymentsInWaiting = useReactiveVar(temporaryPendingPayments)

  const paymentRecords = useQuery(OrderPaymentRecordsDocument, {
    client: atlasClient,
    variables: {
      orderIds: [orderId],
      accountId,
    },
    context: {
      ...(uuid !== undefined && {
        headers: {
          'invoice-auth': uuid,
        },
      }),
    },
    skip: !orderId || !accountId,
    pollInterval: isVisible ? POLL_INTERVAL : 0,
  })

  const externalPaymentRecords = useQuery(ExternalPaymentRecordsDocument, {
    client: atlasClient,
    variables: {
      externalPaymentId,
    },
    skip: !externalPaymentId,
    pollInterval: isVisible ? POLL_INTERVAL : 0,
  })

  const orderPaymentData = transformPaymentRecords(
    paymentRecords.data?.paymentRecords,
  )

  const previousRecordCountRef = useRef(orderPaymentData?.length ?? 0)
  useEffect(() => {
    const currentCount = orderPaymentData?.length ?? 0
    if (currentCount > previousRecordCountRef.current) {
      // A bank transfer's record landing is the `SESSION_COMPLETED` webhook
      // telling us Reach has issued the instructions the retailer is reading
      // right now. Remember the session before the balance we're about to
      // refetch takes the drop-in down with it (sc-260309).
      const session = iframePaymentSession()
      const bankTransferCompletedThisSession = orderPaymentData?.some(
        (record) =>
          record.externalIdentifier === session &&
          record.methodName === BANK_TRANSFER_METHOD_NAME,
      )
      if (session && bankTransferCompletedThisSession) {
        bankTransferSessionAwaitingInstructions(session)
      }
      refetchRemainingBalance({ isPublic: !!uuid })
    }
    previousRecordCountRef.current = currentCount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderPaymentData?.length, uuid])

  const mergedPaymentRecords = mergePaymentsWithPendingPayments({
    queryPaymentRecords: orderPaymentData,
    pendingPaymentRecords: paymentsInWaiting,
  })

  const paymentInfo = getPaymentInfo(orderId, mergedPaymentRecords)

  const extPaymentInfo = getExternalPaymentData(
    externalPaymentRecords.data?.externalPaymentRecords || [],
  )

  // We need to update the stored temporary payments to keep transactions synced
  updateTemporaryPayments({
    orderPaymentData,
    paymentsInWaiting,
    externalPaymentUUID,
    uuid,
  })

  return {
    data: orderId ? paymentInfo! : extPaymentInfo,
    loading: paymentRecords.loading || externalPaymentRecords.loading,
    error: paymentRecords.error || externalPaymentRecords.error,
  }
}

type UseGetManualTransactionsInput = {
  accountId: string
  orderId: string
}

type UseGetManualTransactions = {
  data?: ManualTransactionsQuery['manualPaymentRecords']
  loading: boolean
  error?: Error
}

export const useGetManualTransactions = ({
  accountId,
  orderId,
}: UseGetManualTransactionsInput): UseGetManualTransactions => {
  const { uuid } = useParams<{ uuid?: string }>()
  const isVisible = useIsPageVisible()

  const { data, loading, error } = useQuery(ManualTransactionsDocument, {
    variables: {
      accountId,
      orderIds: [orderId],
    },
    skip: !accountId || !orderId,
    client: atlasClient,
    ...(uuid && {
      context: {
        headers: {
          'invoice-auth': uuid,
        },
      },
    }),
    pollInterval: isVisible ? MANUAL_TRANSACTION_POLL_INTERVAL : 0,
  })
  return { data: data?.manualPaymentRecords, loading, error }
}

atlas`#graphql
  query LocalCurrenciesFFQuery($brandAccountId: ID!) {
    public {
      useJoorPayLocalPayments(brandAccountId: $brandAccountId)
    }
  }
`
